(() => {
  const guardName = "__andskyCartDrawerStableV4";
  if (window[guardName]) return;
  window[guardName] = true;

  // These older custom guards live in cart.js. Mark them as loaded so this
  // earlier, single controller is the only code that mutates the drawer cart.
  window.__andskyCartAjaxGuard = true;
  window.__andskyCartAjaxGuardV2 = true;
  window.__andskyCartGiftPointerGuardV3 = true;
  window.AndCartDrawerFallbackV1 = true;

  const drawerId = "CartDrawer";
  const busyClass = "and-cart-busy";
  const loadingClass = "and-cart-loading";
  let queue = Promise.resolve();

  const root = () => window.Shopify?.routes?.root || "/";
  const cartUrl = (path) => `${root()}${path}`.replace(/([^:]\/)\/+/g, "$1");
  const drawer = () => document.getElementById(drawerId);
  const panel = () => drawer()?.querySelector(".and-cart-panel");
  const sectionId = () => drawer()?.dataset.sectionId || document.querySelector('[id^="MiniCart-"]')?.id.replace("MiniCart-", "") || "";

  const sectionPayload = () => {
    const id = sectionId();
    return {
      sections: id ? [id] : [],
      sections_url: window.location.pathname || "/",
    };
  };

  const setBusy = (state, target) => {
    panel()?.classList.toggle(busyClass, state);
    if (target) {
      target.classList.toggle(loadingClass, state);
      if (state) target.setAttribute("aria-busy", "true");
      else target.removeAttribute("aria-busy");
    }
  };

  const parseJson = async (response) => {
    const text = await response.text();
    let data;
    try {
      data = JSON.parse(text);
    } catch (error) {
      throw new Error(`Cart request returned non-JSON response (${response.status}).`);
    }
    if (!response.ok || data.status >= 400 || data.errors) {
      throw new Error(data.description || data.message || data.errors || "Cart request failed.");
    }
    return data;
  };

  const getJson = (path) =>
    fetch(cartUrl(path), {
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
    }).then(parseJson);

  const postJson = (path, body) => {
    const isFormData = body instanceof FormData;
    return fetch(cartUrl(path), {
      method: "POST",
      credentials: "same-origin",
      headers: isFormData
        ? {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          }
        : {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
      body: isFormData ? body : JSON.stringify(body),
    }).then(parseJson);
  };

  const setDrawerOpen = (open) => {
    const current = drawer();
    if (!current) return;

    if (open) {
      current.hidden = false;
      current.removeAttribute("hidden");
      current.setAttribute("open", "");
      current.classList.add("is-open", "active");
      document
        .querySelectorAll('[aria-controls="CartDrawer"]')
        .forEach((node) => node.setAttribute("aria-expanded", "true"));
      return;
    }

    current.removeAttribute("open");
    current.classList.remove("is-open", "active");
    current.hidden = true;
    current.setAttribute("hidden", "");
    document
      .querySelectorAll('[aria-controls="CartDrawer"]')
      .forEach((node) => node.setAttribute("aria-expanded", "false"));
  };

  const normalizeGiftButtons = (scope = document) => {
    scope.querySelectorAll("#CartDrawer [data-and-gift-variant]").forEach((button) => {
      if (!button.dataset.andGiftVariant) return;
      button.type = "button";
      button.disabled = false;
      button.removeAttribute("disabled");
      button.removeAttribute("aria-disabled");
      button.style.pointerEvents = "";
    });
  };

  const updateCartCount = (count) => {
    document.querySelectorAll("[data-cart-count], .cart-count, cart-count").forEach((node) => {
      node.textContent = count;
      node.hidden = count < 1;
    });
  };

  const replaceNodeContent = (current, next) => {
    if (!current || !next) return;
    current.innerHTML = next.innerHTML;
  };

  const replaceDrawer = (data, keepOpen = true) => {
    const id = sectionId();
    const html = id && data?.sections?.[id];
    if (!html) return;

    const next = new DOMParser().parseFromString(html, "text/html").getElementById(drawerId);
    const current = drawer();
    if (next && current) {
      replaceNodeContent(current.querySelector(".and-cart-header"), next.querySelector(".and-cart-header"));
      replaceNodeContent(current.querySelector(".and-cart-content"), next.querySelector(".and-cart-content"));

      const currentSideRecs = current.querySelector(".and-cart-side-recs");
      const nextSideRecs = next.querySelector(".and-cart-side-recs");
      if (currentSideRecs && nextSideRecs) {
        currentSideRecs.innerHTML = nextSideRecs.innerHTML;
      } else if (currentSideRecs && !nextSideRecs) {
        currentSideRecs.remove();
      } else if (!currentSideRecs && nextSideRecs) {
        current.insertBefore(nextSideRecs, current.querySelector(".and-cart-panel"));
      }

      current.dataset.sectionId = next.dataset.sectionId || current.dataset.sectionId;
      normalizeGiftButtons(current);
    }

    if (typeof data.item_count === "number") updateCartCount(data.item_count);
    if (keepOpen) setDrawerOpen(true);
  };

  const refreshDrawer = async () => {
    const data = await postJson("cart/update.js", {
      updates: {},
      ...sectionPayload(),
    });
    replaceDrawer(data, true);
  };

  const changeLine = async (key, quantity) => {
    const data = await postJson("cart/change.js", {
      id: key,
      quantity,
      ...sectionPayload(),
    });
    replaceDrawer(data, true);
  };

  const addVariant = async (variantId, quantity = 1, properties = {}) => {
    const data = await postJson("cart/add.js", {
      id: Number(variantId),
      quantity,
      properties,
      ...sectionPayload(),
    });
    replaceDrawer(data, true);
  };

  const addProductForm = async (form) => {
    const formData = new FormData(form);
    const id = sectionId();
    if (id) formData.set("sections", id);
    formData.set("sections_url", window.location.pathname || "/");
    const data = await postJson("cart/add.js", formData);
    replaceDrawer(data, true);
  };

  const chooseGift = async (variantId) => {
    const giftButtons = Array.from(document.querySelectorAll("#CartDrawer [data-and-gift-variant]"));
    const giftIds = giftButtons.map((button) => String(button.dataset.andGiftVariant || "")).filter(Boolean);

    giftButtons.forEach((button) => {
      button.classList.toggle("is-selected", String(button.dataset.andGiftVariant) === String(variantId));
    });

    const cart = await getJson("cart.js");
    let selectedLine = null;

    for (const item of cart.items || []) {
      const itemVariantId = String(item.variant_id);
      if (!giftIds.includes(itemVariantId)) continue;

      if (itemVariantId === String(variantId)) {
        selectedLine = item;
        if (item.quantity !== 1) {
          await postJson("cart/change.js", { id: item.key, quantity: 1 });
        }
      } else {
        await postJson("cart/change.js", { id: item.key, quantity: 0 });
      }
    }

    if (!selectedLine) {
      await postJson("cart/add.js", {
        id: Number(variantId),
        quantity: 1,
        properties: { _and_free_gift: "true" },
      });
    }

    await refreshDrawer();
  };

  const run = (action, target) => {
    queue = queue
      .catch(() => {})
      .then(async () => {
        setBusy(true, target);
        try {
          await action();
        } catch (error) {
          console.error("[Andsky cart drawer]", error);
          normalizeGiftButtons();
          setDrawerOpen(true);
        } finally {
          setBusy(false, target);
          normalizeGiftButtons();
        }
      });
  };

  window.addEventListener(
    "click",
    (event) => {
      const currentDrawer = event.target.closest?.("#CartDrawer");
      if (!currentDrawer) return;

      const close = event.target.closest("[data-and-cart-close], .and-cart-overlay");
      if (close || event.target.id === drawerId) {
        event.preventDefault();
        event.stopImmediatePropagation();
        setDrawerOpen(false);
        return;
      }

      const quantityButton = event.target.closest("[data-and-cart-qty][data-and-cart-key]");
      if (quantityButton) {
        event.preventDefault();
        event.stopImmediatePropagation();
        run(() => changeLine(quantityButton.dataset.andCartKey, Number(quantityButton.dataset.andCartQty || 0)), quantityButton);
        return;
      }

      const removeButton = event.target.closest("[data-and-cart-remove]");
      if (removeButton) {
        event.preventDefault();
        event.stopImmediatePropagation();
        run(() => changeLine(removeButton.dataset.andCartRemove, 0), removeButton);
        return;
      }

      const giftButton = event.target.closest("[data-and-gift-variant]");
      if (giftButton) {
        event.preventDefault();
        event.stopImmediatePropagation();
        run(() => chooseGift(giftButton.dataset.andGiftVariant), giftButton);
        return;
      }

      const recButton = event.target.closest("[data-and-rec-add]");
      if (recButton) {
        event.preventDefault();
        event.stopImmediatePropagation();
        run(() => addVariant(recButton.dataset.andRecAdd, 1, {}), recButton);
      }
    },
    true
  );

  window.addEventListener(
    "submit",
    (event) => {
      const discountForm = event.target.closest?.("#CartDrawer [data-and-discount-form]");
      if (discountForm) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const code = discountForm.querySelector('input[name="discount"]')?.value.trim();
        if (code) window.location.href = `${root()}discount/${encodeURIComponent(code)}?redirect=/cart`;
        return;
      }

      const productForm = event.target.closest?.('form[action*="/cart/add"]');
      if (!productForm || productForm.closest("#CartDrawer")) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      const submitter = event.submitter || productForm.querySelector('[type="submit"], button[name="add"], button');
      run(() => addProductForm(productForm), submitter);
    },
    true
  );

  if (!document.getElementById("and-cart-drawer-stability-style")) {
    const style = document.createElement("style");
    style.id = "and-cart-drawer-stability-style";
    style.textContent = `
      #CartDrawer .and-cart-panel.and-cart-busy {
        cursor: wait;
      }
      #CartDrawer .and-cart-panel.and-cart-busy button,
      #CartDrawer .and-cart-panel.and-cart-busy input,
      #CartDrawer .and-cart-panel.and-cart-busy textarea {
        pointer-events: none;
      }
      #CartDrawer .and-cart-loading {
        opacity: .6;
      }
    `;
    document.head.appendChild(style);
  }

  document.addEventListener("DOMContentLoaded", () => normalizeGiftButtons());
  new MutationObserver(() => normalizeGiftButtons()).observe(document.documentElement, { childList: true, subtree: true });
})();
